import React, { useCallback, useEffect, useState } from 'react';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { FormattedMessage } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import FileUploadButton, {
  UploadedDocument
} from 'src/components/FileUploadButton';

interface DocumentItem {
  id: number;
  originalName: string;
  contentType: string;
  sizeBytes: number;
  createdAt: string;
}

interface Props {
  installationId: number;
  checklistItemId: number;
  subtaskKey: string;
}

function fileIcon(contentType: string) {
  if (contentType === 'application/pdf')
    return <PictureAsPdfIcon fontSize="small" color="error" />;
  return <InsertDriveFileIcon fontSize="small" />;
}

function SubtaskDocumentUpload({ installationId, checklistItemId, subtaskKey }: Props) {
  const [docs, setDocs] = useState<DocumentItem[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchDocs = useCallback(() => {
    axiosConfig
      .get('/GetDocuments', { params: { checklistItemId, subtaskKey } })
      .then((res) => {
        if (Array.isArray(res.data)) setDocs(res.data);
      })
      .catch(() => setDocs([]));
  }, [checklistItemId, subtaskKey]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs, refreshKey]);

  const handleDownload = (doc: DocumentItem) => {
    axiosConfig
      .get('/DownloadDocument', {
        params: { id: doc.id },
        responseType: 'blob'
      })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.originalName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  const handleUploaded = (_doc: UploadedDocument) => {
    setRefreshKey((k) => k + 1);
  };

  return (
    <Box mt={0.5} mb={1}>
      {docs.length > 0 && (
        <Stack spacing={0.25} mb={0.75}>
          {docs.map((d) => (
            <Stack
              key={d.id}
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ pl: 4 }}
            >
              {fileIcon(d.contentType)}
              <Typography variant="body2" sx={{ flex: 1 }} noWrap>
                {d.originalName}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleDownload(d)}
                aria-label="download"
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      )}
      <Box sx={{ pl: 4 }}>
        <FileUploadButton
          scope={1}
          installationId={installationId}
          checklistItemId={checklistItemId}
          subtaskKey={subtaskKey}
          onUploaded={handleUploaded}
        />
      </Box>
      {docs.length === 0 && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ pl: 4, display: 'block', mt: 0.5 }}
        >
          <FormattedMessage
            id="checklistNoAttachments"
            defaultMessage="No file attached yet."
          />
        </Typography>
      )}
    </Box>
  );
}

export default SubtaskDocumentUpload;
